<template>
  <div
    class="mx"
    :class="[{ 'mx--pinned': pinned, 'mx--drawer': drawerOpen, 'mx--dragging': dragging }]"
  >
    <!-- Fondo: dos lavados casi neutros. Sin algo detrás, el vidrio se ve plano. -->
    <div class="mx-field" aria-hidden="true"><i class="w1"></i><i class="w2"></i><i class="gr"></i></div>
    <div class="mx-scrim" @click="drawerOpen = false"></div>

    <!-- ══ ESPINAZO ══════════════════════════════════════════════
         62px en reposo. El hover lo abre por encima del contenido sin
         reacomodar nada; "Fijar" lo abre reacomodando la grilla. -->
    <aside class="mx-spine">
      <div class="mx-glass"></div>

      <button type="button" class="mx-spine__top" :title="pinned ? 'Soltar el panel' : 'Fijar el panel'" @click="pinned = !pinned">
        <span class="mx-mark">HF</span>
        <span class="mx-lbl mx-spine__name">Mensajería</span>
      </button>

      <div class="mx-spine__scroll" ref="spineScroll">
        <nav class="mx-stack" style="margin-bottom:4px">
          <button type="button" class="mx-tile" :class="{ 'is-on': view === 'panel' }" title="Panel" @click="goPanel">
            <span class="mx-tile__ico">▦</span>
            <span class="mx-lbl">Panel</span>
            <span v-if="queue.length" class="mx-tile__n">{{ queue.length }}</span>
          </button>
          <!-- Los hilos entre anfitrión y huésped. Son otra cosa que el resto de
               la consola —no hay secciones, ni etapas, ni bot— así que entran
               como una vista aparte y no como una sección más del espinazo. -->
          <button type="button" class="mx-tile" :class="{ 'is-on': view === 'hostguest' }" title="Mensajes entre anfitrión y huésped" @click="goHostGuest">
            <span class="mx-tile__ico">⇄</span>
            <span class="mx-lbl">Anfitrión y huésped</span>
          </button>
        </nav>

        <div class="mx-eyebrow">
          <span>Secciones</span>
          <!-- Crear/editar/borrar secciones solo lo deja la API al superadmin
               (ensureSuperAdmin en ConversationSectionController), así que
               mostrarle los controles a un admin normal sería ofrecerle un 403. -->
          <button
            v-if="isSuperAdmin"
            type="button"
            class="mx-eyebrow__add"
            title="Crear una sección"
            @click.stop="openCreateSection"
          >
            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>
        <nav class="mx-stack">
          <button
            v-for="s in visibleSections"
            :key="s.status"
            type="button"
            class="mx-tile"
            :class="{
              'is-on': activeStatus === s.status && view !== 'panel',
              'is-drop': dropSection === s.status,
              'is-editable': isSuperAdmin
            }"
            :title="s.label"
            @click="openSection(s)"
            @dragover.prevent="dropSection = s.status"
            @dragleave="dropSection === s.status && (dropSection = null)"
            @drop.prevent="onDropSection(s)"
          >
            <span class="mx-tile__ico mx-tile__mk">{{ (s.label || '?').trim().charAt(0).toUpperCase() }}</span>
            <span class="mx-lbl">{{ s.label }}</span>
            <span
              v-if="s.instance"
              class="mx-tile__conn"
              :class="connOk(s) ? 'is-up' : 'is-down'"
              :title="s.instance.nombre + ' · ' + (s.estado_conexion || 'desconocido')"
            ></span>
            <span class="mx-tile__n">{{ countOf(s.status) }}</span>
            <!-- La placa ya es un <button>: estos van como <span> porque un
                 botón adentro de otro no es HTML válido y el navegador rompe
                 el árbol. El .stop es lo que evita que editar abra la sección.
                 Los íconos son SVG y no glifos: el ✎ salía finito y el 🗑 es un
                 emoji, que se pinta con su propio color y no se puede teñir de
                 rojo ni levantar el contraste. -->
            <span v-if="isSuperAdmin" class="mx-tile__acts">
              <span
                class="mx-tile__act"
                role="button"
                title="Editar la sección"
                @click.stop="openEditSection(s)"
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
                </svg>
              </span>
              <!-- La principal representa "todas" y es a donde caen las
                   conversaciones de lo que se borra: el backend rechaza
                   eliminarla (422), así que ni se ofrece. -->
              <span
                v-if="!isPrincipal(s)"
                class="mx-tile__act is-danger"
                role="button"
                title="Eliminar la sección"
                @click.stop="openDeleteSection(s)"
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
              </span>
            </span>
          </button>
        </nav>

        <div class="mx-eyebrow mx-eyebrow--low"><span>Directorio</span></div>
        <nav class="mx-stack">
          <button type="button" class="mx-tile" title="Clientes" @click="showClientes = true">
            <span class="mx-tile__ico">○</span><span class="mx-lbl">Clientes</span>
          </button>
          <button type="button" class="mx-tile" title="Anfitriones" @click="showAnfitriones = true">
            <span class="mx-tile__ico">○</span><span class="mx-lbl">Anfitriones</span>
          </button>
          <button type="button" class="mx-tile" title="Alojamientos" @click="showAlojamientos = true">
            <span class="mx-tile__ico">○</span><span class="mx-lbl">Alojamientos</span>
          </button>
          <button v-if="isSuperAdmin" type="button" class="mx-tile" title="Instancias" @click="showInstancias = true">
            <span class="mx-tile__ico">○</span><span class="mx-lbl">Instancias</span>
          </button>
        </nav>
      </div>

    </aside>

    <!-- ══ BARRA SUPERIOR ════════════════════════════════════════ -->
    <header class="mx-top">
      <div class="mx-glass"></div>

      <!-- El ☰ solo se ve cuando el espinazo es un cajón. Vive acá adentro, en
           el flujo de la barra: flotando abajo a la izquierda se le sentaba
           encima al clip de adjuntar del compositor. -->
      <button type="button" class="mx-act mx-act--menu" title="Secciones" aria-label="Secciones" @click="drawerOpen = !drawerOpen">☰</button>

      <!-- Volver. El glifo ⟨ de antes era un carácter de texto: fino, mal
           alineado y del mismo gris que el rótulo, así que el botón no se leía
           como botón. Ahora es un chevron dibujado, y la flecha se corre a la
           izquierda al pasar el mouse: la dirección se ve antes de leer. -->
      <button v-if="view !== 'panel'" type="button" class="mx-act mx-act--back" :title="backTitle" @click="goBack">
        <svg class="mx-act__arrow" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        <span class="mx-lab">{{ view === 'chat' ? 'Tablero' : 'Panel' }}</span>
      </button>

      <div class="mx-where">
        <h1>{{ title }}</h1>
        <em v-if="subtitle" :title="subtitleTip">{{ subtitle }}</em>
      </div>

      <!-- Host-huésped trae su propio buscador y no entiende ni las secciones
           ni el rango de fechas de acá: en esa vista la barra queda sin
           búsqueda, y el hueco es lo que sigue empujando los botones a la
           derecha (mx-group no tiene margen propio). -->
      <div v-if="view !== 'hostguest'" class="mx-find">
        <span class="mx-find__i">⌕</span>
        <span v-for="c in chips" :key="c.k" class="mx-chip">
          {{ c.label }}
          <button type="button" title="Quitar filtro" @click="clearChip(c.k)">✕</button>
        </span>
        <input v-model="q" :placeholder="searchPlaceholder" />
        <button v-if="q" type="button" class="mx-find__x" title="Limpiar" @click="q = ''">✕</button>
      </div>
      <div v-else class="mx-find-gap" aria-hidden="true"></div>

      <div class="mx-group">
        <div v-if="view !== 'hostguest'" class="mx-pop-wrap">
          <button type="button" class="mx-act" :class="{ 'is-on': popOpen === 'f' || chips.length }" title="Filtros" @click.stop="toggleFilters">
            ⚙<span class="mx-lab">Filtros</span>
            <span v-if="chips.length" class="mx-act__n">{{ chips.length }}</span>
          </button>
          <div v-if="popOpen === 'f'" class="mx-pop" @click.stop>
            <div class="mx-pop__r">
              <div class="mx-pop__l">Origen del lead</div>
              <div class="mx-pills">
                <button
                  v-for="o in origins"
                  :key="o.v || 'all'"
                  type="button"
                  class="mx-pill"
                  :class="{ 'is-on': origin === o.v }"
                  @click="origin = origin === o.v ? '' : o.v"
                >{{ o.label }}</button>
              </div>
            </div>

            <!-- Rango de fechas del último movimiento de sección. Es el filtro
                 del tablero de siempre: los atajos aplican solos y el rango a
                 mano espera el botón. En el tablero lo resuelve el BACKEND
                 (?desde/?hasta), así que los totales de cada columna y el
                 scroll infinito llegan ya filtrados. -->
            <div class="mx-pop__r">
              <div class="mx-pop__l">Último movimiento</div>
              <div class="mx-pills">
                <button
                  v-for="p in datePresets"
                  :key="p.key"
                  type="button"
                  class="mx-pill"
                  :class="{ 'is-on': activeDatePreset === p.key }"
                  @click="applyDatePreset(p.key)"
                >{{ p.label }}</button>
              </div>
              <label class="mx-dt">
                <span>Desde</span>
                <input class="mx-in" type="date" v-model="draftDates.desde" :max="draftDates.hasta || ''" />
              </label>
              <label class="mx-dt">
                <span>Hasta</span>
                <input class="mx-in" type="date" v-model="draftDates.hasta" :min="draftDates.desde || ''" />
              </label>
            </div>

            <div class="mx-pop__f">
              <button
                type="button"
                class="mx-btn"
                :disabled="!draftDates.desde && !draftDates.hasta"
                @click="applyDates"
              >Aplicar rango</button>
              <button type="button" class="mx-link" @click="clearAllChips">Quitar todos</button>
            </div>
          </div>
        </div>

        <!-- En angosto estos dos dejan de significar algo: la lista la maneja el
             estado del chat (o ves la lista, o ves el hilo) y la ficha se abre
             como hoja desde el ▤ de la cabecera del hilo. -->
        <template v-if="view === 'chat' && !narrow">
          <span class="mx-sep"></span>
          <button type="button" class="mx-act" :class="{ 'is-on': listOn }" title="Mostrar u ocultar la lista" @click="listOn = !listOn">
            ☰<span class="mx-lab">Lista</span>
          </button>
          <button type="button" class="mx-act" :class="{ 'is-on': railOn }" title="Mostrar u ocultar la ficha" @click="railOn = !railOn">
            ▤<span class="mx-lab">Ficha</span>
          </button>
        </template>

        <button type="button" class="mx-act" title="Refrescar" :disabled="loading" @click="refresh">⟳</button>

        <!-- Única salida del módulo: la consola ocupa toda la pantalla, así que
             sin esto no habría cómo volver al resto del backoffice. Va al final
             de todo, que es donde se busca una salida, y separada del resto de
             los botones para no apretarla queriendo refrescar. -->
        <span class="mx-sep"></span>
        <button type="button" class="mx-out" title="Volver al panel de administración" @click="exit">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" />
          </svg>
          Salir
        </button>
      </div>
    </header>

    <!-- ══ ESCENARIO ═════════════════════════════════════════════
         El arrastre se escucha acá arriba: cuando empieza, el espinazo se
         abre solo, porque en ese momento deja de ser navegación y pasa a
         ser el destino. -->
    <div
      class="mx-stage"
      @dragstart.capture="dragging = true"
      @dragend.capture="dragging = false; dropSection = null"
      @drop.capture="dragging = false"
    >
      <!-- ── PANEL ── -->
      <div v-if="view === 'panel'" class="mx-panel" ref="panelScroll">
        <div class="mx-panel__wrap">

          <div class="mx-hello">
            <div>
              <h2>Buenas, {{ firstName }}</h2>
              <p v-if="isSuperAdmin && downInstances.length">
                <b>{{ downInstances.length }}</b>
                {{ downInstances.length === 1 ? 'instancia está caída' : 'instancias están caídas' }}
                y esas secciones no reciben nada. Empezá por ahí.
              </p>
              <p v-else-if="isSuperAdmin">
                Todas las instancias están arriba. <b>{{ totalConversations }}</b> conversaciones en el equipo.
              </p>
              <p v-else-if="queue.length">
                Tenés <b>{{ queue.length }}</b> conversaciones esperando respuesta. Arrancá por arriba.
              </p>
              <p v-else>Nadie está esperando respuesta. Buen momento para empujar las reservas.</p>
            </div>
            <div class="mx-hello__go">
              <div class="mx-hello__when">
                {{ today }}<br>{{ isSuperAdmin ? realSections.length + ' secciones · ' + totalConversations + ' conversaciones' : currentSectionLabel }}
              </div>
              <button type="button" class="mx-act mx-act--line" @click="openBoardCurrent">Abrir tablero</button>
            </div>
          </div>

          <!-- Cifras -->
          <div class="mx-stats">
            <template v-if="isSuperAdmin">
              <div class="mx-stat mx-stat--flat" style="--tone: rgba(184,122,14,.2)">
                <span class="mx-stat__k"><i class="warn"></i>Esperan respuesta</span>
                <span class="mx-stat__v">{{ agg.waiting == null ? '—' : agg.waiting }}</span>
                <span class="mx-stat__d">
                  <template v-if="agg.waiting == null">Sin datos del servidor todavía</template>
                  <template v-else>En <b>{{ agg.waitingSections }}</b> secciones · <b>{{ agg.risk }}</b> ya en riesgo</template>
                </span>
              </div>
              <div class="mx-stat mx-stat--flat" style="--tone: rgba(27,156,107,.18)">
                <span class="mx-stat__k"><i class="ok"></i>Conversaciones</span>
                <span class="mx-stat__v">{{ totalConversations }}</span>
                <!-- realSections y no visibleSections: la principal significa
                     "todas", contarla como una sección más miente. -->
                <span class="mx-stat__d">En <b>{{ realSections.length }}</b> secciones</span>
              </div>
              <div class="mx-stat mx-stat--flat" :style="`--tone: ${downInstances.length ? 'rgba(217,58,52,.2)' : 'rgba(27,156,107,.18)'}`">
                <span class="mx-stat__k"><i :class="downInstances.length ? 'risk' : 'ok'"></i>Instancias</span>
                <span class="mx-stat__v">{{ downInstances.length ? downInstances.length + ' caídas' : 'todas arriba' }}</span>
                <span class="mx-stat__d">{{ downInstances.length ? downInstances.map(s => s.label).join(' · ') : instances.length + ' conectadas' }}</span>
              </div>
            </template>
            <template v-else>
              <button
                v-for="c in brokerStats"
                :key="c.k"
                type="button"
                class="mx-stat"
                :class="{ 'is-on': qFilter === c.k }"
                :style="`--tone: ${c.tone}`"
                @click="qFilter = qFilter === c.k ? null : c.k"
              >
                <span class="mx-stat__k"><i :class="c.dot"></i>{{ c.label }}</span>
                <span class="mx-stat__v">{{ c.value }}</span>
                <span class="mx-stat__d" v-html="c.detail"></span>
              </button>
            </template>
          </div>

          <!-- Tabla de secciones (superadmin) -->
          <section v-if="isSuperAdmin" class="mx-pane">
            <div class="mx-pane__h">
              <span class="mx-pane__t">Cómo viene cada sección</span>
              <span class="mx-pane__s">{{ agg.waiting == null ? 'carga e instancias en vivo · faltan los agregados' : 'arriba lo que más urge' }} · clic para entrar</span>
              <!-- Sin esto, aplicar un rango acá parece que no hizo nada: los
                   números salen de /support/sections y /support/stats/sections,
                   que todavía no reciben fechas. Mejor decirlo que dejar al
                   filtro pareciendo roto. -->
              <span v-if="hasDateFilter" class="mx-pane__warn">
                {{ dateFilterLabel }}: el rango no aplica a estos números, entrá a una sección para verlo
              </span>
            </div>
            <div class="mx-tb">
              <div class="mx-tb__h">
                <span>Sección</span><span>Carga</span><span>Sin responder</span><span>1er contacto</span><span>En riesgo</span>
              </div>
              <button
                v-for="s in adminRows"
                :key="s.status"
                type="button"
                class="mx-tb__r"
                :class="{ 'is-down': s.down }"
                @click="openSection(s)"
              >
                <span class="mx-tb__s">
                  <i class="mx-tb__dot" :class="s.down ? 'is-down' : 'is-up'"></i>{{ s.label }}
                  <em v-if="s.down">sin WhatsApp</em>
                </span>
                <span class="mx-tb__load">
                  <b>{{ s.total }}</b>
                  <i class="mx-tb__bar"><i :style="`width:${s.pct}%`"></i></i>
                </span>
                <span class="mx-tb__n" :class="s.waitTone">{{ s.wait }}</span>
                <span class="mx-tb__fc" :class="s.fcTone">{{ s.fcText }}</span>
                <span class="mx-tb__n" :class="s.riskTone">{{ s.risk }}</span>
              </button>
            </div>
          </section>

          <!-- Cola del broker -->
          <section v-else class="mx-pane">
            <div class="mx-pane__h">
              <span class="mx-pane__t">Tu cola</span>
              <span class="mx-pane__s">{{ qFilter ? 'filtrada · ' : '' }}ordenada por tiempo sin responder</span>
              <button v-if="qFilter" type="button" class="mx-act" @click="qFilter = null">Ver todas</button>
            </div>
            <div class="mx-pane__b mx-q" ref="queueScroll">
              <div v-if="loading" class="mx-empty">Cargando tu cola…</div>
              <div v-else-if="!filteredQueue.length" class="mx-empty">
                Nadie está esperando respuesta con estos filtros.
              </div>
              <button
                v-for="c in filteredQueue"
                :key="c.id"
                type="button"
                class="mx-qrow"
                @click="openChat(c)"
              >
                <span class="mx-qrow__bar" :class="c._risk"></span>
                <span class="mx-qrow__av">{{ ini(c.client_name || c.client_identifier) }}</span>
                <span class="mx-qrow__l1">
                  <span class="mx-qrow__nm">{{ c.client_name || convPhone(c) }}</span>
                  <span class="mx-qrow__st">{{ sectionLabel(c.status) }}</span>
                </span>
                <span class="mx-qrow__msg">{{ preview(c) }}</span>
                <span class="mx-qrow__why">
                  <span class="mx-qrow__t" :class="c._risk === 'ok' ? '' : c._risk">{{ dur(c._wait) }}</span>
                  <span class="mx-qrow__lab">sin responder</span>
                </span>
              </button>
            </div>
          </section>

        </div>
      </div>

      <!-- ── TABLERO ── -->
      <BoardV2
        v-else-if="view === 'board'"
        ref="board"
        :status="activeStatus"
        :instance="activeInstance"
        :label="currentSectionLabel"
        :sections="visibleSections"
        :can-manage-stages="isSuperAdmin"
        :is-super-admin="isSuperAdmin"
        :query="q"
        :origin="origin"
        :desde="dates.desde"
        :hasta="dates.hasta"
        @open-chat="onBoardOpenChat"
        @move-to-section="onMoveToSection"
      />

      <!-- ── ANFITRIÓN ↔ HUÉSPED ──
           El componente viene del dashboard anterior y trae su propia lista,
           su hilo y su estética: acá adentro es una vista entera, sin la barra
           lateral de secciones ni el buscador de arriba. -->
      <div v-else-if="view === 'hostguest'" class="mx-hg">
        <HostGuestMessages ref="hostguest" />
      </div>

      <!-- ── CHAT ── -->
      <ChatV2
        v-else
        :status="activeStatus"
        :section-label="currentSectionLabel"
        :conversation-id="openId"
        :list-on="listOn"
        :rail-on="railOn"
        :rail-sheet="railSheet"
        :narrow="narrow"
        :is-super-admin="isSuperAdmin"
        :query="q"
        :origin="origin"
        :desde="dates.desde"
        :hasta="dates.hasta"
        @open="id => openId = id"
        @changed="refreshCounts"
        @toggle-rail="toggleRail"
        @error="msg => say('Algo salió mal', msg, true)"
        @ok="msg => say('Listo', msg)"
      />
    </div>

    <ClientesPopup v-model="showClientes" :base-url="BASE_URL" />
    <AlojamientosPopup v-model="showAlojamientos" :base-url="BASE_URL" />
    <AnfitrionesPopup v-model="showAnfitriones" :base-url="BASE_URL" />
    <InstancesPopup v-model="showInstancias" :base-url="BASE_URL" :sections="sections" @changed="loadSections" />

    <!-- ══ SECCIONES: crear / editar / eliminar ══════════════════
         Mismo modal para los tres, como el de columnas del tablero: cambian
         el encabezado y qué se manda, no la forma. -->
    <transition name="mx-fade">
      <div v-if="secModal.open && secModal.mode !== 'delete'" class="mx-ov" @click.self="closeSecModal">
        <div class="mx-modal">
          <div class="mx-modal__h">
            <span class="mx-modal__g">{{ secModal.mode === 'create' ? '＋' : '✎' }}</span>
            <div>
              <h3>{{ secModal.mode === 'create' ? 'Nueva sección' : 'Editar sección' }}</h3>
              <p v-if="secModal.mode === 'create'">Arranca con las columnas de siempre y sin conversaciones.</p>
              <p v-else>Cambiar el nombre no mueve nada: las conversaciones se enlazan por id.</p>
            </div>
          </div>
          <div class="mx-modal__b">
            <label class="mx-fld">
              <span>Nombre</span>
              <input
                ref="secNameInput"
                v-model="secModal.nombre"
                class="mx-in"
                type="text"
                maxlength="100"
                placeholder="Ej: Anfitriones"
                @keydown.enter="saveSection"
              />
            </label>
            <label class="mx-fld">
              <!-- El correo no es un dato de contacto: es el permiso. Un admin
                   que no es superadmin solo ve las secciones cuyo correo es el
                   suyo, acá y en la API. -->
              <span>Correo del responsable</span>
              <input
                v-model="secModal.correo"
                class="mx-in"
                type="email"
                maxlength="255"
                placeholder="Ej: soporte@houseandflats.com"
                @keydown.enter="saveSection"
              />
            </label>
            <label v-if="secModal.mode === 'edit'" class="mx-fld">
              <span>Instancia de WhatsApp</span>
              <select v-model="secModal.instanceId" class="mx-in">
                <option value="">— Sin instancia —</option>
                <option v-for="inst in instances" :key="inst.id" :value="String(inst.id)">
                  {{ inst.nombre }}{{ inst.profile_name ? ' · ' + inst.profile_name : '' }} ({{ inst.estado_conexion || 'desconocido' }})
                </option>
              </select>
            </label>
            <p v-if="secModal.error" class="mx-err">{{ secModal.error }}</p>
          </div>
          <div class="mx-modal__f">
            <button type="button" class="mx-btn" :disabled="secModal.saving" @click="closeSecModal">Cancelar</button>
            <button type="button" class="mx-btn mx-btn--go" :disabled="secModal.saving" @click="saveSection">
              {{ secModal.saving ? 'Guardando…' : (secModal.mode === 'create' ? 'Crear sección' : 'Guardar') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="mx-fade">
      <div v-if="secModal.open && secModal.mode === 'delete'" class="mx-ov" @click.self="closeSecModal">
        <div class="mx-modal">
          <div class="mx-modal__h">
            <span class="mx-modal__g is-risk">🗑</span>
            <div>
              <h3>Eliminar "{{ secModal.nombre }}"</h3>
              <p>No se pierde ninguna conversación: pasan a la sección principal.</p>
            </div>
          </div>
          <div class="mx-modal__b">
            <p class="mx-note">
              La sección tiene <b>{{ countOf(secModal.slug) }}</b>
              {{ countOf(secModal.slug) === 1 ? 'conversación' : 'conversaciones' }}.
              Se mueven a la principal y sus columnas se borran con ella.
            </p>
            <p v-if="secModal.error" class="mx-err">{{ secModal.error }}</p>
          </div>
          <div class="mx-modal__f">
            <button type="button" class="mx-btn" :disabled="secModal.saving" @click="closeSecModal">Cancelar</button>
            <button type="button" class="mx-btn mx-btn--risk" :disabled="secModal.saving" @click="saveSection">
              {{ secModal.saving ? 'Eliminando…' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="mx-toast">
      <div v-if="toast" class="mx-toast" :class="{ 'is-bad': toast.bad }">
        <span><b>{{ toast.title }}</b>{{ toast.text }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * Mensajería v2 — armazón y panel.
 *
 * Es la consola de mensajería: vive en /admin/mensajeria desde que se eliminó
 * el dashboard anterior. Monta BoardV2 (tablero) y ChatV2 (hilo), reusa los
 * popups del directorio tal cual, y hereda de la consola vieja la vista de
 * mensajes anfitrión ↔ huésped (HostGuestMessages), que no tiene equivalente
 * propio acá.
 */
import apiUrls from '@/new-constants/apiUrls'
import BoardV2 from './BoardV2.vue'
import ChatV2 from './ChatV2.vue'
import ClientesPopup from '../components/ClientesPopup.vue'
import AlojamientosPopup from '../components/AlojamientosPopup.vue'
import AnfitrionesPopup from '../components/Anfitrionespopup.vue'
import InstancesPopup from '../components/InstancesPopup.vue'
/* Lo único que quedó del dashboard viejo: los hilos anfitrión ↔ huésped no
   tienen equivalente acá, así que el componente se reusa tal cual. */
import HostGuestMessages from '../components/HostGuestMessages.vue'
import {
  ini, dur, waitingSecs, stageSecs, riskOf, convPhone, matches, originLabel, authHeaders,
  DATE_PRESETS, presetRange, shortDate, inDateRange
} from './ui'

const BASE_URL = apiUrls.base

/**
 * El umbral de "pantalla angosta", palabra por palabra igual que el bloque
 * ANGOSTO de mx.css. Hay dos cosas que el CSS no puede decidir solo y que
 * dependen de esto: si la ficha del lead es una columna o una hoja, y qué
 * significa el botón ▤. Si las dos copias se separan, el ▤ empieza a mover el
 * estado equivocado.
 *
 * Va por ancho O por puntero: una tablet de 1200px con dedo no puede abrir un
 * panel por hover, igual que un celular.
 */
const MX_NARROW = '(max-width: 1024px), (hover: none) and (max-width: 1366px)'

/**
 * Dónde se guardan los filtros de la barra.
 *
 * Son globales a la consola: valen para el panel, para cualquier sección y
 * para la próxima vez que se entre. Un filtro que se borra solo al cambiar de
 * pantalla obliga a rearmarlo cada vez, y peor: deja mirando números que uno
 * cree filtrados y no lo están.
 *
 * Van en localStorage y no en la URL a propósito: la consola es una sola ruta
 * y el estado tiene que sobrevivir a un F5, no viajar en un link.
 */
const MX_FILTROS_KEY = 'mensajeria_v2_filtros'

export default {
  name: 'DashboardV2',
  components: { BoardV2, ChatV2, ClientesPopup, AlojamientosPopup, AnfitrionesPopup, InstancesPopup, HostGuestMessages },

  data () {
    return {
      BASE_URL,
      view: 'panel',              // panel | board | chat | hostguest
      activeStatus: '',
      activeInstance: null,
      openId: null,

      sections: [],
      instances: [],
      counts: {},                 // { slug de sección: total } — de /support/sections
      mine: [],                   // conversaciones asignadas a mí
      agg: { waiting: null, risk: 0, waitingSections: 0, rows: null },

      q: '',
      origin: '',
      /* El rango de fechas del último movimiento de sección, igual que en el
         tablero de siempre: `dates` es el rango APLICADO —el que viaja al
         backend como ?desde/?hasta— y `draftDates` es lo que se está eligiendo
         en el menú hasta que se aprieta "Aplicar rango". Separarlos es lo que
         evita que el tablero se recargue en cada tecleo del calendario. */
      dates: { desde: '', hasta: '' },
      draftDates: { desde: '', hasta: '' },
      qFilter: null,
      popOpen: null,

      pinned: false,
      listOn: true,
      railOn: true,
      /* La ficha como hoja, en angosto. Es un estado aparte de railOn a
         propósito: el gusto del broker en el escritorio no tiene que decidir
         si en el celular se le abre una hoja en la cara cada vez que toca una
         conversación. Arranca cerrada, siempre. */
      railSheet: false,
      narrow: false,
      drawerOpen: false,
      dropSection: null,
      dragging: false,

      /* Los cuatro popups del Directorio. Van declarados acá sí o sí: Vue 2 no
         hace reactivo lo que se le asigna a una propiedad que no existía al
         crear la instancia, así que sin esto `showClientes = true` cambiaba el
         valor pero no repintaba nada. Se veía como que el popup no abría —y
         cuando algún otro cambio forzaba un render, aparecía y ya no se podía
         cerrar, porque el ✕ tampoco repintaba. */
      showClientes: false,
      showAnfitriones: false,
      showAlojamientos: false,
      showInstancias: false,

      /* Alta, edición y borrado de secciones, en un solo modal.
         mode: 'create' | 'edit' | 'delete'. `slug` se guarda además del id
         porque es la clave con la que el resto de la consola habla de una
         sección (activeStatus, counts): el id solo lo usa la API. */
      secModal: {
        open: false,
        mode: null,
        id: null,
        slug: '',
        nombre: '',
        correo: '',
        instanceId: '',
        error: '',
        saving: false
      },

      loading: false,
      toast: null,
      _toastTimer: null,

      origins: [
        { v: '', label: 'Todos' },
        { v: 'erasmusplay', label: 'ErasmusPlay' },
        { v: 'meta', label: 'Meta' },
        { v: 'directo', label: 'Directo' }
      ]
    }
  },

  computed: {
    isSuperAdmin () { return !!this.$store.getters.isSuperAdmin },
    currentUserEmail () {
      let email = localStorage.getItem('correo') || ''
      if (!email) {
        try {
          const u = JSON.parse(localStorage.getItem('usuario') || '{}')
          email = u.correo || u.email || ''
        } catch (e) { email = '' }
      }
      return String(email).trim().toLowerCase()
    },
    firstName () {
      try {
        const u = JSON.parse(localStorage.getItem('usuario') || '{}')
        const n = u.nombre || u.name || ''
        if (n) return String(n).split(' ')[0]
      } catch (e) { /* sin datos de sesión */ }
      return this.currentUserEmail ? this.currentUserEmail.split('@')[0] : 'equipo'
    },
    today () {
      const d = new Date()
      const dias = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
      const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
      return `${dias[d.getDay()]} ${d.getDate()} de ${meses[d.getMonth()]}`
    },

    /** Un admin normal solo ve las secciones cuyo correo es el suyo. */
    visibleSections () {
      if (this.isSuperAdmin || !this.currentUserEmail) return this.sections
      return this.sections.filter(s => String(s.correo || '').trim().toLowerCase() === this.currentUserEmail)
    },
    currentSectionLabel () {
      const s = this.sections.find(x => x.status === this.activeStatus)
      return s ? s.label : 'Conversaciones'
    },
    /**
     * La sección principal (id=1 / bot_sophi / seccion_principal) no es una
     * sección más: representa "todas", y su conteo es el total global. Hay que
     * poder separarla o cualquier suma la cuenta dos veces.
     */
    principalSection () {
      return this.visibleSections.find(s => s.seccion_principal || s.id === 1 || s.status === 'bot_sophi') || null
    },
    /** Las secciones de verdad, sin la que significa "todas". */
    realSections () {
      return this.visibleSections.filter(s => s !== this.principalSection)
    },
    /**
     * Antes esto sumaba TODAS las secciones incluida la principal, así que el
     * total salía duplicado. Si la principal está a la vista su conteo ya es el
     * total global (incluye las conversaciones sin sección); si no, se suman
     * las que quedan.
     */
    totalConversations () {
      if (this.principalSection) return this.countOf(this.principalSection.status)
      return this.realSections.reduce((a, s) => a + this.countOf(s.status), 0)
    },
    downInstances () {
      return this.visibleSections.filter(s => s.instance && !this.connOk(s))
    },

    title () {
      if (this.view === 'panel') return 'Panel'
      if (this.view === 'board') return this.currentSectionLabel
      if (this.view === 'hostguest') return 'Anfitrión y huésped'
      return 'Conversaciones'
    },
    subtitle () {
      if (this.view === 'board') return String(this.countOf(this.activeStatus))
      if (this.view === 'chat') return this.currentSectionLabel
      return ''
    },
    subtitleTip () {
      return this.view === 'board'
        ? `${this.countOf(this.activeStatus)} conversaciones en ${this.currentSectionLabel}`
        : ''
    },
    searchPlaceholder () {
      if (this.view === 'board') return 'Buscar en todas las columnas: nombre, teléfono o intake…'
      if (this.view === 'chat') return 'Buscar conversación…'
      return 'Buscar un lead por nombre, teléfono o intake…'
    },
    backTitle () {
      return this.view === 'chat' ? `Volver al tablero de ${this.currentSectionLabel}` : 'Volver al panel'
    },
    chips () {
      const out = []
      if (this.origin) {
        const o = this.origins.find(x => x.v === this.origin)
        out.push({ k: 'origin', label: o ? o.label : this.origin })
      }
      if (this.hasDateFilter) out.push({ k: 'dates', label: this.dateFilterLabel })
      return out
    },

    // ─── Rango de fechas (last_move_seccion) ───────────────────
    datePresets () { return DATE_PRESETS },
    hasDateFilter () { return !!(this.dates.desde || this.dates.hasta) },
    /**
     * Lo que dice el chip. Si el rango aplicado es igual a un atajo se muestra
     * el nombre del atajo ("Últimos 7 días"), que se lee mejor que dos fechas;
     * si no, el rango en dd/mm.
     */
    dateFilterLabel () {
      const { desde, hasta } = this.dates
      const preset = DATE_PRESETS.find(p => {
        const r = presetRange(p.key)
        return r.desde === desde && r.hasta === hasta
      })
      if (preset) return preset.label
      if (desde && hasta) {
        return desde === hasta ? shortDate(desde) : `${shortDate(desde)} – ${shortDate(hasta)}`
      }
      if (desde) return `Desde ${shortDate(desde)}`
      if (hasta) return `Hasta ${shortDate(hasta)}`
      return 'Fechas'
    },
    /** El atajo que coincide con lo que hay cargado en el menú, para pintarlo. */
    activeDatePreset () {
      const found = DATE_PRESETS.find(p => {
        const r = presetRange(p.key)
        return r.desde === this.draftDates.desde && r.hasta === this.draftDates.hasta
      })
      return found ? found.key : null
    },

    /** La cola: quién espera respuesta, el que hace más rato primero. */
    queue () {
      return this.mine
        .map(c => Object.assign({}, c, {
          _wait: waitingSecs(c),
          _stage: stageSecs(c),
          _risk: riskOf(c, false)
        }))
        .filter(c => c._wait != null && c._wait > 0)
        .sort((a, b) => b._wait - a._wait)
    },
    filteredQueue () {
      return this.queue.filter(c => {
        if (this.origin && c.lead_origin !== this.origin) return false
        /* La cola no la puede filtrar el servidor: ?desde/?hasta solo lo
           entiende el endpoint del tablero. Se aplica acá con el mismo
           criterio (ver inDateRange en ui.js). */
        if (!inDateRange(c, this.dates.desde, this.dates.hasta)) return false
        if (!matches(c, this.q)) return false
        if (this.qFilter === 'wait' && c._wait < 14400) return false
        if (this.qFilter === 'risk' && c._risk !== 'risk') return false
        return true
      })
    },
    brokerStats () {
      const waiting = this.queue.length
      const oldest = this.queue.length ? this.queue[0]._wait : 0
      const atRisk = this.queue.filter(c => c._risk === 'risk').length
      const urgent = this.mine.filter(c => c.is_urgent).length
      return [
        { k: 'wait', dot: 'warn', tone: 'rgba(184,122,14,.2)', label: 'Esperan respuesta', value: waiting,
          detail: waiting ? `La más vieja hace <b>${dur(oldest)}</b>` : 'Estás al día' },
        { k: 'risk', dot: 'risk', tone: 'rgba(217,58,52,.2)', label: 'En riesgo', value: atRisk,
          detail: atRisk ? 'Más de 24 h sin responder' : 'Ninguna pasó el límite' },
        { k: 'urg', dot: 'ok', tone: 'rgba(27,156,107,.18)', label: 'Marcadas urgentes', value: urgent,
          detail: urgent ? 'Revisalas primero' : 'Sin urgencias' }
      ]
    },

    /** Filas de la tabla del superadmin: primero lo roto, después lo que más presiona. */
    adminRows () {
      const maxN = this.visibleSections.reduce((m, s) => Math.max(m, this.countOf(s.status)), 1)
      const rows = this.visibleSections.map(s => {
        const extra = (this.agg.rows && this.agg.rows[s.status]) || null
        const total = this.countOf(s.status)
        const wait = extra ? extra.waiting : null
        const risk = extra ? extra.risk : null
        const fcS = extra ? extra.first_contact_avg : null
        return {
          status: s.status, label: s.label, id: s.id, instance: s.instance,
          down: !!(s.instance && !this.connOk(s)),
          total, pct: Math.max(3, Math.round(total / maxN * 100)),
          wait: wait == null ? '—' : wait,
          risk: risk == null ? '—' : risk,
          fcText: fcS == null ? (extra ? 'sin datos' : '—') : dur(fcS),
          fcTone: fcS == null ? 'is-off' : fcS > 7200 ? 'is-risk' : fcS > 1800 ? 'is-warn' : 'is-ok',
          waitTone: wait == null ? '' : wait >= 60 ? 'is-risk' : wait >= 20 ? 'is-warn' : '',
          riskTone: risk == null ? '' : risk >= 20 ? 'is-risk' : risk >= 5 ? 'is-warn' : '',
          _sort: (wait || 0) + (risk || 0) * 3
        }
      })
      return rows.sort((a, b) => (b.down - a.down) || (b._sort - a._sort) || (b.total - a.total))
    }
  },

  watch: {
    view (v) { if (v === 'panel') this.loadMine() },
    /* Los tres filtros de la barra se guardan apenas cambian. No hace falta
       debounce en la búsqueda: es una escritura a localStorage de unos bytes,
       y perderla por cerrar la pestaña justo después sería peor. */
    q () { this.saveFilters() },
    origin () { this.saveFilters() },
    dates: { deep: true, handler () { this.saveFilters() } }
  },

  created () {
    try {
      this.pinned = localStorage.getItem('mensajeria_v2_pinned') === '1'
      // La consola quedó en un solo tema (día). Se borra la preferencia vieja
      // para no dejar basura en el navegador de quien lo había probado.
      localStorage.removeItem('mensajeria_v2_theme')
    } catch (e) { /* modo privado */ }

    /* Antes de pedir nada: los filtros se restauran primero para que la
       primera carga del tablero ya salga con el rango puesto y no haya que
       pedirlo dos veces. */
    this.restoreFilters()

    /* openFromRoute necesita las secciones ya cargadas para poder pararse en la
       del lead, por eso va encadenado y no suelto como los demás. */
    this.loadSections().then(() => this.openFromRoute())
    this.loadInstances()
    this.loadCounts()
    this.loadMine()
    this.loadAggregates()

    document.addEventListener('click', this.closePop)
    document.addEventListener('keydown', this.onKey)

    /* matchMedia avisa al cruzar el umbral, y también al hacer zoom en el
       navegador: el zoom cambia el viewport medido en píxeles CSS, así que
       mueve los mismos breakpoints que rotar un celular. */
    this._mq = window.matchMedia(MX_NARROW)
    if (this._mq.addEventListener) this._mq.addEventListener('change', this.syncNarrow)
    else if (this._mq.addListener) this._mq.addListener(this.syncNarrow)
    this.narrow = this._mq.matches

    window.addEventListener('resize', this.onResize)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.measure)
      window.visualViewport.addEventListener('scroll', this.measure)
    }
  },
  mounted () { this.measure() },
  beforeDestroy () {
    document.removeEventListener('click', this.closePop)
    document.removeEventListener('keydown', this.onKey)
    if (this._mq) {
      if (this._mq.removeEventListener) this._mq.removeEventListener('change', this.syncNarrow)
      else if (this._mq.removeListener) this._mq.removeListener(this.syncNarrow)
    }
    window.removeEventListener('resize', this.onResize)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.measure)
      window.visualViewport.removeEventListener('scroll', this.measure)
    }
    if (this._toastTimer) clearTimeout(this._toastTimer)
  },

  methods: {
    ini, dur, convPhone, originLabel,

    closePop () { this.popOpen = null },
    onKey (e) {
      if (e.key !== 'Escape') return
      /* Los popups del Directorio son ventanas encima de todo: son lo primero
         que tiene que caer, antes que cualquier capa de la consola. */
      if (this.showClientes || this.showAnfitriones || this.showAlojamientos || this.showInstancias) {
        this.showClientes = false
        this.showAnfitriones = false
        this.showAlojamientos = false
        this.showInstancias = false
        return
      }
      /* El modal de secciones también es una ventana encima de todo, y encima
         puede estar guardando: cerrarlo con Esc no cancela el pedido, pero
         tampoco hace falta —el que llega tarde solo refresca la lista. */
      if (this.secModal.open) { this.closeSecModal(); return }
      if (this.popOpen) { this.popOpen = null; return }
      /* Lo que está encima de todo cae primero: el cajón del espinazo y la hoja
         de la ficha son capas, y tienen que irse antes que los filtros. */
      if (this.drawerOpen) { this.drawerOpen = false; return }
      if (this.railSheet) { this.railSheet = false; return }
      if (this.q) { this.q = ''; return }
      if (this.qFilter) { this.qFilter = null; return }
      if (this.view === 'chat') { this.view = 'board'; return }
      if (this.view === 'board') { this.view = 'panel' }
    },

    // ─── Viewport ────────────────────────────────────────────
    /**
     * El alto real de la ventana, en una variable CSS.
     *
     * El teclado del celular no encoge el viewport de layout, así que el
     * compositor termina abajo del teclado y se escribe a ciegas. 100dvh no
     * alcanza: descuenta la barra del navegador, no el teclado. El único que
     * lo sabe es visualViewport.
     *
     * Se escribe a mano sobre el elemento y no con :style porque el evento
     * 'scroll' de visualViewport dispara muy seguido: pasarlo por el estado
     * reactivo sería un re-render por cuadro al desplazarse.
     */
    measure () {
      if (!this.$el || !this.$el.style) return
      const vv = window.visualViewport
      /* Con pinch-zoom el visualViewport es solo el recorte que se ve, y ahí no
         hay nada que encoger: el navegador ya está agrandando todo. Encogiendo
         la consola al recorte, se metería adentro de la lupa. */
      const h = (vv && vv.scale <= 1.02) ? vv.height : window.innerHeight
      if (h > 0) this.$el.style.setProperty('--mx-vh', Math.round(h) + 'px')
    },
    onResize () { this.measure(); this.syncNarrow() },
    /**
     * Cruzar el umbral cambia qué es la ficha del lead: columna o hoja. Al
     * angostarse arranca cerrada; al ensancharse el cajón sobra, porque el
     * espinazo vuelve a estar fijo en su lugar.
     */
    syncNarrow () {
      const n = this._mq ? this._mq.matches : window.matchMedia(MX_NARROW).matches
      if (n === this.narrow) return
      this.narrow = n
      if (n) this.railSheet = false
      else this.drawerOpen = false
    },
    /**
     * El ▤ mueve dos cosas distintas según haya lugar: la columna de la ficha
     * en una pantalla ancha, la hoja encima del hilo en una angosta.
     */
    toggleRail () {
      if (this.narrow) this.railSheet = !this.railSheet
      else this.railOn = !this.railOn
    },
    connOk (s) {
      const e = String(s.estado_conexion || '').trim().toLowerCase()
      return ['open', 'conectado', 'connected', 'online'].indexOf(e) >= 0
    },
    countOf (status) {
      const n = this.counts[status]
      return n == null ? 0 : n
    },
    sectionLabel (status) {
      const s = this.sections.find(x => x.status === status)
      return s ? s.label : status
    },
    preview (c) {
      const t = String(c.last_message || '').trim()
      return t || 'Sin mensajes todavía'
    },

    // ─── Navegación ──────────────────────────────────────────
    /**
     * Vuelve de donde vino. Si ya estamos ahí, no hace nada y no tira error.
     *
     * La consola es a pantalla completa (sin header ni menú de admin), así que
     * "Salir" es la única salida. Quien entró desde el dashboard de intakes
     * espera volver ahí, no al backoffice: eso lo dice ?from, que pone el botón
     * de conversación del intake. Sin ?from, al backoffice como siempre.
     */
    exit () {
      const from = (this.$route && this.$route.query) ? String(this.$route.query.from || '') : ''
      const destino = from === 'intakes' ? 'dashboard-intakes' : 'admin'
      this.$router.push({ name: destino }).catch(() => {})
    },
    /* Ni acá ni en openSection se limpia la búsqueda: los filtros de la barra
       son globales y siguen puestos al moverse entre el panel y las secciones.
       Lo que se ve filtrado está siempre declarado arriba, en el chip y en la
       caja de búsqueda. */
    goPanel () { this.view = 'panel'; this.drawerOpen = false },
    goHostGuest () { this.view = 'hostguest'; this.drawerOpen = false },
    goBack () { this.view = this.view === 'chat' ? 'board' : 'panel' },
    openSection (s) {
      this.activeStatus = s.status
      this.activeInstance = null
      this.view = 'board'
      /* qFilter sí se limpia: es de las tres cifras del panel del broker, no
         de la barra, y no significa nada adentro de un tablero. */
      this.qFilter = null
      this.drawerOpen = false
    },
    openBoardCurrent () {
      if (!this.activeStatus && this.visibleSections.length) this.activeStatus = this.visibleSections[0].status
      if (this.activeStatus) this.view = 'board'
    },
    /* Al abrir una conversación la hoja de la ficha arranca cerrada: en el
       celular tapa el hilo, y lo primero que el broker quiere ver es el hilo. */
    openChat (c) {
      this.activeStatus = c.status || this.activeStatus
      this.openId = c.id
      this.view = 'chat'
      this.railSheet = false
      this.drawerOpen = false
    },
    onBoardOpenChat (card) {
      this.openId = card && card.id
      this.view = 'chat'
      this.railSheet = false
      this.drawerOpen = false
    },

    /**
     * Deep-link: llegar con ?conversation=<id> y abrir ese chat directo.
     *
     * Lo usa el botón de conversación del dashboard de intakes, que además
     * manda ?section=<slug> (ya sabe en qué sección está el lead) y ?from para
     * poder volver. Se llama cuando las secciones YA están cargadas: el chat se
     * abre dentro de una sección, así que sin la lista no se puede posicionar.
     *
     * Con ?section no hace falta pedir nada. Sin él, se resuelve preguntando por
     * la conversación; si eso falla igual se abre el chat, nada más que en la
     * sección que estuviera activa.
     */
    async openFromRoute () {
      const q = (this.$route && this.$route.query) ? this.$route.query : {}
      const id = q.conversation ? String(q.conversation) : ''
      if (!id) return

      let slug = q.section ? String(q.section) : ''

      if (!slug) {
        try {
          const res = await fetch(`${apiUrls.conversationsSupport}/${id}`, { headers: authHeaders() })
          const json = await res.json()
          const conv = (json && json.data) ? json.data : null
          const s = (conv && conv.section_id != null)
            ? this.sections.find(x => Number(x.id) === Number(conv.section_id))
            : null
          if (s) slug = s.status
        } catch (e) {
          /* Se abre igual: mejor el chat en la sección equivocada que nada. */
        }
      }

      if (slug && this.sections.some(s => s.status === slug)) {
        this.activeStatus = slug
      }

      // El resto del componente guarda `openId` como número (viene de card.id);
      // la query siempre llega como string.
      const n = Number(id)
      this.openId = Number.isFinite(n) && String(n) === id ? n : id
      this.view = 'chat'
      this.railSheet = false
      this.drawerOpen = false
    },

    /**
     * Mover conversaciones a otra sección. Lo dispara tanto el muelle del
     * tablero como soltar una tarjeta sobre una placa del espinazo.
     */
    async onMoveToSection (payload) {
      const ids = (payload && payload.ids) || []
      const sectionId = payload && payload.sectionId
      if (!ids.length || !sectionId) return
      try {
        const res = await fetch(`${apiUrls.conversationsSupport}/move-section`, {
          method: 'PATCH',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            ids,
            section_id: sectionId,
            changed_by: Number(localStorage.getItem('idusuario')) || null
          })
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || 'No se pudieron mover las conversaciones.')
        const dest = this.sections.find(s => String(s.id) === String(sectionId))
        this.loadCounts()
        if (this.$refs.board && this.$refs.board.fetchBoard) this.$refs.board.fetchBoard()
        this.say(
          ids.length > 1 ? ids.length + ' conversaciones movidas' : 'Conversación movida',
          dest ? 'Ahora ' + (ids.length > 1 ? 'están' : 'está') + ' en ' + dest.label + '.' : ''
        )
      } catch (e) {
        this.say('No se pudieron mover', e.message, true)
      }
    },

    clearChip (k) {
      if (k === 'origin') this.origin = ''
      if (k === 'dates') this.clearDateFilter()
    },
    clearAllChips () {
      this.origin = ''
      this.clearDateFilter()
      this.popOpen = null
    },

    // ─── Rango de fechas (last_move_seccion) ─────────────────
    /* Al abrir, el menú arranca mostrando el rango que está aplicado: si no,
       los campos quedarían vacíos al lado de un chip que dice lo contrario. */
    toggleFilters () {
      if (this.popOpen === 'f') { this.popOpen = null; return }
      this.draftDates = Object.assign({}, this.dates)
      this.popOpen = 'f'
    },
    /* Los atajos no esperan el botón: elegir "Hoy" ya es la decisión completa.
       El rango a mano sí, porque hasta que no están las dos puntas el filtro
       todavía no significa nada. */
    applyDatePreset (key) {
      this.draftDates = presetRange(key)
      this.applyDates()
    },
    applyDates () {
      if (!this.draftDates.desde && !this.draftDates.hasta) return
      this.dates = Object.assign({}, this.draftDates)
      this.popOpen = null
      /* El aviso no es adorno. En el panel del superadmin el rango no mueve
         ni un número —los agregados por sección no lo reciben— así que sin
         esto aplicar el filtro se siente como que el botón no hace nada. */
      this.say('Filtro aplicado', this.view === 'panel'
        ? ` ${this.dateFilterLabel}. Entrá a una sección para ver el tablero filtrado.`
        : ` Último movimiento: ${this.dateFilterLabel}.`)
    },
    clearDateFilter () {
      this.dates = { desde: '', hasta: '' }
      this.draftDates = { desde: '', hasta: '' }
    },

    // ─── Los filtros, entre visitas ──────────────────────────
    saveFilters () {
      try {
        localStorage.setItem(MX_FILTROS_KEY, JSON.stringify({
          q: this.q,
          origin: this.origin,
          desde: this.dates.desde,
          hasta: this.dates.hasta
        }))
      } catch (e) { /* modo privado o cuota llena: el filtro sigue valiendo en la sesión */ }
    },
    restoreFilters () {
      let f = null
      try {
        f = JSON.parse(localStorage.getItem(MX_FILTROS_KEY) || 'null')
      } catch (e) { f = null /* dato viejo o corrupto: se arranca limpio */ }
      if (!f || typeof f !== 'object') return

      this.q = typeof f.q === 'string' ? f.q : ''
      /* El origen se valida contra la lista: un valor viejo que ya no existe
         dejaría un chip que no se puede sacar desde el menú. */
      this.origin = this.origins.some(o => o.v && o.v === f.origin) ? f.origin : ''
      /* El rango se guarda en fechas absolutas, no como "Hoy". Es a propósito:
         si ayer elegiste "Hoy", hoy tenés que seguir viendo ese día y no el de
         hoy. El chip lo dice con la fecha cuando ya no coincide con el atajo. */
      const fecha = v => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) ? v : ''
      this.dates = { desde: fecha(f.desde), hasta: fecha(f.hasta) }
      this.draftDates = Object.assign({}, this.dates)
    },

    // ─── Datos ───────────────────────────────────────────────
    async loadSections () {
      try {
        const res = await fetch(`${BASE_URL}support/sections`, { headers: authHeaders() })
        const json = await res.json()
        const rows = (json && json.data) ? json.data : (Array.isArray(json) ? json : [])
        this.sections = rows.map(s => Object.assign({}, s, {
          label: s.label || this.prettify(s.status)
        }))
        /**
         * La carga por sección sale de acá y no de `?status=stats`.
         *
         * Ese endpoint agrupa por la columna legacy `conversation_agent_client
         * .status`, que es el enum active|pending|escalated|reopened|cold|
         * closed|erasmusplay. Nosotros buscábamos por slug de sección (kevin,
         * anfi, sophi_2…), así que la clave NUNCA coincidía y todas las
         * secciones mostraban 0. El número correcto ya venía en /support/
         * sections como `count`, calculado por section_id.
         */
        const counts = {}
        this.sections.forEach(s => { counts[s.status] = Number(s.count) || 0 })
        this.counts = counts

        if (!this.activeStatus && this.visibleSections.length) {
          this.activeStatus = this.visibleSections[0].status
        }
      } catch (e) {
        this.say('No se pudieron cargar las secciones', 'Revisá la conexión y probá de nuevo.', true)
      }
    },
    async loadInstances () {
      try {
        const res = await fetch(`${BASE_URL}support/instances`, { headers: authHeaders() })
        const json = await res.json()
        this.instances = (json && json.data) ? json.data : (Array.isArray(json) ? json : [])
      } catch (e) { this.instances = [] }
    },
    /**
     * La carga por sección viene junto con las secciones (ver loadSections),
     * así que refrescar los conteos es volver a pedir las secciones. Se deja
     * el nombre porque es lo que llaman refresh() y los movimientos de tarjeta.
     */
    loadCounts () { return this.loadSections() },
    /** Mis conversaciones, para la cola del panel. */
    async loadMine () {
      const id = localStorage.getItem('idusuario') || ''
      if (!id) { this.mine = []; return }
      this.loading = true
      try {
        const params = new URLSearchParams({ filter: 'mine', agent_id: id, per_page: '100' })
        const res = await fetch(`${apiUrls.conversationsSupport}?${params}`, { headers: authHeaders() })
        const json = await res.json()
        this.mine = (json && json.data) ? json.data : []
      } catch (e) {
        this.mine = []
        this.say('No se pudo cargar tu cola', 'Revisá la conexión y probá de nuevo.', true)
      } finally {
        this.loading = false
      }
    },
    /**
     * Agregados por sección para el panel del superadmin.
     *
     * Los sirve GET /support/stats/sections cruzando tres tablas: los mensajes
     * (sin responder y 1er contacto), conversation_stage_history (cuánto lleva
     * quieta en la etapa) y las conversaciones con sus secciones (la carga).
     *
     * Si el endpoint no está —un ambiente sin deployar todavía— devuelve 404 y
     * esto no rompe: la tabla ya sabe mostrarse con "—" en esas columnas.
     */
    async loadAggregates () {
      if (!this.isSuperAdmin) return
      try {
        const res = await fetch(`${BASE_URL}support/stats/sections`, { headers: authHeaders() })
        if (!res.ok) return
        const json = await res.json()
        const rows = (json && json.data) ? json.data : []
        const byStatus = {}
        let waiting = 0
        let risk = 0
        let sections = 0
        rows.forEach(r => {
          byStatus[r.status] = r
          /* La fila de la sección principal se saltea al sumar: cuenta TODAS
             las conversaciones, así que sumarla junto al resto duplicaba el
             total de la tarjeta de arriba. */
          if (r.is_principal) return
          waiting += (r.waiting || 0)
          risk += (r.risk || 0)
          if (r.waiting > 0) sections++
        })
        this.agg = { waiting, risk, waitingSections: sections, rows: byStatus }
      } catch (e) { /* sin agregados: la tabla ya sabe mostrarse sin ellos */ }
    },

    refresh () {
      this.loadCounts()
      this.loadInstances()
      if (this.view === 'panel') { this.loadMine(); this.loadAggregates() }
      if (this.view === 'board' && this.$refs.board && this.$refs.board.fetchBoard) this.$refs.board.fetchBoard()
      // Host-huésped trae su propia carga: el ⟳ de la barra tiene que mover
      // eso y no solo los conteos de secciones, que ahí no se ven.
      if (this.view === 'hostguest' && this.$refs.hostguest && this.$refs.hostguest.fetchConversations) {
        this.$refs.hostguest.fetchConversations()
      }
      this.say('Actualizado', 'Se volvieron a pedir los datos.')
    },
    refreshCounts () { this.loadCounts() },

    onDropSection (s) {
      this.dropSection = null
      this.dragging = false
      if (this.$refs.board && this.$refs.board.dropOnSection) this.$refs.board.dropOnSection(s)
    },

    // ─── Secciones: crear, editar, eliminar ──────────────────
    /**
     * La principal (id=1 / bot_sophi) no es una sección más: representa
     * "todas" y es el destino de las conversaciones de lo que se borra. Se
     * puede renombrar, pero no eliminar —la API devuelve 422 si se intenta.
     */
    isPrincipal (s) {
      return !!(s && (s.seccion_principal || s.id === 1 || s.status === 'bot_sophi'))
    },
    openCreateSection () {
      this.secModal = {
        open: true, mode: 'create', id: null, slug: '',
        nombre: '', correo: '', instanceId: '', error: '', saving: false
      }
      this.drawerOpen = false
      this.focusSecName()
    },
    openEditSection (s) {
      if (!s || !s.id) return
      this.secModal = {
        open: true,
        mode: 'edit',
        id: s.id,
        slug: s.status,
        nombre: s.label || s.status,
        correo: s.correo || '',
        /* El enlace con la instancia se guarda del lado de la instancia
           (id_dueño), no de la sección: por eso sale de s.instance y no de
           un campo propio. */
        instanceId: s.instance ? String(s.instance.id) : '',
        error: '',
        saving: false
      }
      this.drawerOpen = false
      this.focusSecName()
    },
    openDeleteSection (s) {
      if (!s || !s.id || this.isPrincipal(s)) return
      this.secModal = {
        open: true, mode: 'delete', id: s.id, slug: s.status,
        nombre: s.label || s.status, correo: '', instanceId: '', error: '', saving: false
      }
      this.drawerOpen = false
    },
    closeSecModal () {
      this.secModal.open = false
      this.secModal.mode = null
      this.secModal.error = ''
    },
    focusSecName () {
      this.$nextTick(() => {
        const el = this.$refs.secNameInput
        if (el && el.focus) el.focus()
      })
    },
    /**
     * Los tres caminos terminan igual: se vuelven a pedir las secciones (que
     * es de donde salen también los conteos) y las instancias, porque editar
     * puede haber cambiado de dueño a una de ellas.
     */
    async saveSection () {
      const m = this.secModal
      if (m.saving) return

      /* Se copia lo que hace falta ANTES del await: cerrar el modal con Esc
         mientras el pedido viaja deja el objeto en blanco, y después no habría
         con qué decidir si la sección abierta es la que se borró. */
      const mode = m.mode
      const id = m.id
      const slug = m.slug
      const nombre = String(m.nombre || '').trim()
      const correo = String(m.correo || '').trim()
      const instanceId = m.instanceId || null

      if (mode !== 'delete') {
        if (!nombre) { m.error = 'Poné un nombre para la sección.'; return }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
          m.error = 'El correo no parece válido.'
          return
        }
      }

      m.error = ''
      m.saving = true
      try {
        let res
        if (mode === 'create') {
          res = await fetch(`${BASE_URL}support/sections`, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ nombre, correo })
          })
        } else if (mode === 'edit') {
          res = await fetch(`${BASE_URL}support/sections/${id}`, {
            method: 'PATCH',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ nombre, correo, instance_id: instanceId })
          })
        } else {
          res = await fetch(`${BASE_URL}support/sections/${id}`, {
            method: 'DELETE',
            headers: authHeaders()
          })
        }
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || 'No se pudo guardar la sección.')

        const wasActive = slug && this.activeStatus === slug
        this.closeSecModal()
        /* loadSections trae de una el nombre nuevo y los conteos: el rótulo de
           la barra y el número del espinazo salen de ahí. Renombrar no cambia
           el slug, así que la sección abierta sigue siendo la misma y no hay
           que moverse de lugar. */
        await this.loadSections()
        this.loadInstances()
        this.loadAggregates()

        if (mode === 'delete' && wasActive) {
          /* La sección que se estaba mirando ya no existe: quedarse en su
             tablero sería pedirle al backend un slug muerto. */
          this.activeStatus = this.visibleSections.length ? this.visibleSections[0].status : ''
          this.view = 'panel'
        }

        this.say(
          mode === 'create' ? 'Sección creada' : mode === 'edit' ? 'Sección actualizada' : 'Sección eliminada',
          mode === 'create' ? ` "${nombre}" ya está en el espinazo, con las columnas de siempre.`
            : mode === 'delete' ? ' Sus conversaciones pasaron a la sección principal.'
              : ' Los cambios quedaron guardados.'
        )
      } catch (e) {
        /* El modal puede haberse cerrado con Esc mientras viajaba el pedido:
           ahí el error se cuenta con el toast, que es lo único a la vista. */
        if (this.secModal.open) this.secModal.error = e.message || 'No se pudo guardar la sección.'
        else this.say('No se pudo guardar la sección', ' ' + (e.message || ''), true)
      } finally {
        this.secModal.saving = false
      }
    },

    prettify (status) {
      return String(status || '').replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())
    },
    say (title, text, bad) {
      this.toast = { title, text: text || '', bad: !!bad }
      if (this._toastTimer) clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => { this.toast = null }, 3400)
    }
  }
}
</script>

<style src="./mx.css"></style>
